<template>
  <div class="certificate-builder">
    <!-- Left Sidebar (same in both modes) -->
    <div class="sidebar">
      <div class="sidebar-tools">
        <button @click="loadTemplate" class="tool-btn" title="Open Template" :disabled="isPreviewMode">
          <svg viewBox="0 0 24 24">
            <path d="M5 4h14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2m0 2v12h14V6H5m7 2l4 4h-3v4h-2v-4H8l4-4z" />
          </svg>
        </button>
        <button @click="handleSave" class="tool-btn" title="Save Template" :disabled="isPreviewMode || isSaving || isSaveDisabled">
          <svg v-if="!isSaving" viewBox="0 0 24 24">
            <path d="M5 4h14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2m0 2v12h14V6H5m7 12l-4-4h3V8h2v6h3l-4 4z" />
          </svg>
          <span v-else class="save-spinner"></span>
        </button>
        <span class="sidebar-sep"></span>
        <button @click="addPlaceholder" class="tool-btn" title="Add Placeholder" :disabled="isPreviewMode">
          <svg viewBox="0 0 24 24">
            <path d="M4 4h6v2H6v12h4v2H4V4zm10 0h6v16h-6v-2h4V6h-4V4zM9 11h6v2H9v-2z" />
          </svg>
        </button>
        <button @click="addText" class="tool-btn" title="Add Text" :disabled="isPreviewMode">
          <svg viewBox="0 0 24 24">
            <path d="M5 4v3h5.5v12h3V7H19V4H5z" />
          </svg>
        </button>
        <button @click="addImage" class="tool-btn" title="Add Image" :disabled="isPreviewMode">
          <svg viewBox="0 0 24 24">
            <path d="M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2m0 2v14h14V5H5m3 4a2 2 0 1 1 0 4 2 2 0 0 1 0-4m10 10H6l3-4 2 2.5L14 12l4 7z" />
          </svg>
        </button>
        <button @click="deleteElement" class="tool-btn" :disabled="!selectedElement || isPreviewMode" title="Delete">
          <svg viewBox="0 0 24 24">
            <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM8 9h8v10H8V9zm7.5-5l-1-1h-5l-1 1H5v2h14V4h-3.5z" />
          </svg>
        </button>
        <span class="sidebar-sep"></span>
        <button @click="togglePreview" class="tool-btn preview-btn" :class="{ active: isPreviewMode }" :title="isPreviewMode ? 'Exit Preview' : 'Preview'">
          <svg viewBox="0 0 24 24">
            <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-3-1.34-3-3z" />
          </svg>
        </button>
        <span class="sidebar-sep"></span>
        <button @click="toggleFullscreen" class="tool-btn" :class="{ active: isFullscreen }" :title="isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'">
          <svg v-if="!isFullscreen" viewBox="0 0 24 24">
            <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" />
          </svg>
          <svg v-else viewBox="0 0 24 24">
            <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Canvas Area (Builder Mode) -->
    <div class="canvas-area" ref="canvasArea" v-show="!isPreviewMode">
      <div class="canvas-wrap" :style="{ width: canvasWidth * scale + 'px', height: canvasHeight * scale + 'px' }">
        <div class="canvas" ref="canvas"
          :style="{ width: canvasWidth + 'px', height: canvasHeight + 'px', background: canvasBg, transform: `scale(${scale})`, transformOrigin: 'top left' }"
          @mousedown="onCanvasMouseDown" @mousemove="onMouseMove" @mouseup="onMouseUp" @mouseleave="onMouseUp">

          <!-- Smart Guides -->
          <div v-if="guides.centerX" class="guide guide-v guide-center" :style="{ left: canvasWidth / 2 + 'px' }"></div>
          <div v-if="guides.centerY" class="guide guide-h guide-center" :style="{ top: canvasHeight / 2 + 'px' }"></div>
          <div v-for="(g, i) in guides.vertical" :key="'v'+i" class="guide guide-v guide-align" :style="{ left: g + 'px' }"></div>
          <div v-for="(g, i) in guides.horizontal" :key="'h'+i" class="guide guide-h guide-align" :style="{ top: g + 'px' }"></div>

          <div v-for="el in elements" :key="el.id" class="el" :data-id="el.id"
            :class="{ selected: selectedElement && selectedElement.id === el.id }" :style="getStyle(el)"
            @mousedown.stop="startDrag(el, $event)">
            <img v-if="el.type === 'image'" :src="el.src" :style="{ width: el.width + 'px', height: el.height + 'px' }"
              draggable="false" />
            <template v-else>{{ el.content }}</template>
            <div v-if="selectedElement && selectedElement.id === el.id && el.type === 'image'" class="resize"
              @mousedown.stop="startResize($event)"></div>
          </div>
        </div>
      </div>

      <!-- Floating Properties Popover -->
      <div v-if="selectedElement && !dragging && !resizing" class="props-popover" :style="popoverStyle" @mousedown.stop @input="notifyChange" @change="notifyChange">
        <div class="props">
          <div class="prop-row"><label>X</label><input type="number" v-model.number="selectedElement.x" /></div>
          <div class="prop-row"><label>Y</label><input type="number" v-model.number="selectedElement.y" /></div>
          <template v-if="selectedElement.type !== 'image'">
            <div class="prop-row">
              <label>W</label><input type="number" v-model.number="selectedElement.width" min="0" :placeholder="'auto'" />
            </div>
            <select v-model="selectedElement.fontFamily">
              <option v-for="f in fonts" :key="f" :value="f">{{ f }}</option>
            </select>
            <div class="prop-row">
              <input type="number" v-model.number="selectedElement.fontSize" min="8" max="200" />
              <span class="unit">px</span>
              <input type="color" v-model="selectedElement.color" class="color" />
            </div>
            <div class="prop-row style-row">
              <button @click="selectedElement.fontWeight = selectedElement.fontWeight === 'bold' ? 'normal' : 'bold'; notifyChange()"
                :class="{ active: selectedElement.fontWeight === 'bold' }">B</button>
              <button @click="selectedElement.fontStyle = selectedElement.fontStyle === 'italic' ? 'normal' : 'italic'; notifyChange()"
                :class="{ active: selectedElement.fontStyle === 'italic' }" class="i">I</button>
              <span class="sep-v"></span>
              <button @click="selectedElement.textAlign = 'left'; notifyChange()" :class="{ active: selectedElement.textAlign === 'left' }">
                <svg viewBox="0 0 24 24">
                  <path d="M3 3h18v2H3V3zm0 4h12v2H3V7zm0 4h18v2H3v-2zm0 4h12v2H3v-2zm0 4h18v2H3v-2z" />
                </svg>
              </button>
              <button @click="selectedElement.textAlign = 'center'; notifyChange()" :class="{ active: selectedElement.textAlign === 'center' }">
                <svg viewBox="0 0 24 24">
                  <path d="M3 3h18v2H3V3zm3 4h12v2H6V7zm-3 4h18v2H3v-2zm3 4h12v2H6v-2zm-3 4h18v2H3v-2z" />
                </svg>
              </button>
              <button @click="selectedElement.textAlign = 'right'; notifyChange()" :class="{ active: selectedElement.textAlign === 'right' }">
                <svg viewBox="0 0 24 24">
                  <path d="M3 3h18v2H3V3zm6 4h12v2H9V7zm-6 4h18v2H3v-2zm6 4h12v2H9v-2zm-6 4h18v2H3v-2z" />
                </svg>
              </button>
            </div>
            <input v-if="selectedElement.type === 'placeholder'" type="text" v-model="selectedElement.placeholderName"
              @input="selectedElement.content = `{{${selectedElement.placeholderName}}}`" placeholder="PlaceholderName" />
            <textarea v-else v-model="selectedElement.content" placeholder="Text..."></textarea>
          </template>
          <template v-else>
            <div class="prop-row"><label>W</label><input type="number" v-model.number="selectedElement.width" min="10" /></div>
            <div class="prop-row"><label>H</label><input type="number" v-model.number="selectedElement.height" min="10" /></div>
            <button @click="replaceImage" class="btn-replace">Replace Image</button>
          </template>
        </div>
      </div>
    </div>

    <!-- Preview Area (Preview Mode) -->
    <div class="preview-area" ref="previewArea" v-show="isPreviewMode" @click="onPreviewAreaClick">
      <div class="preview-container" ref="previewContainer">
        <CertificatePreview
          v-if="isPreviewMode"
          :templateContent="previewContent"
          :placeholders="filledPlaceholders"
          :maxWidth="canvasWidth"
          :maxHeight="canvasHeight"
          :scaleX="previewScale"
          :scaleY="previewScale"
          :editable="true"
        />
      </div>
      <!-- Preview Placeholder Popover -->
      <div v-if="editingPlaceholder" class="ph-popover" :style="phPopoverStyle" @mousedown.stop>
        <textarea
          ref="phPopoverInput"
          :value="previewData[editingPlaceholder] || ''"
          @input="onPlaceholderInput(editingPlaceholder, $event.target.value)"
          :placeholder="'Enter ' + editingPlaceholder"
          rows="2"
        ></textarea>
      </div>
    </div>

    <!-- Inputs -->
    <input type="file" ref="imgInput" accept="image/*" style="display:none" @change="onImageFile" />
    <input type="file" ref="tplInput" accept=".html,.htm" style="display:none" @change="onTemplateFile" />

    <!-- Placeholder Modal -->
    <div v-if="showPhModal" class="cb-modal-bg" @click.self="showPhModal = false">
      <div class="cb-modal">
        <div class="cb-modal-head">Add Placeholder</div>
        <div class="cb-modal-body">
          <input type="text" v-model="newPhName" placeholder="e.g. StudentName" @keyup.enter="addPh" ref="phInput" />
          <div class="cb-hint">Creates: <code v-pre>{{ newPhName ? `{{${newPhName }}}`:'{{...}}'}}</code></div>
        </div>
        <div class="cb-modal-foot">
          <button @click="showPhModal = false">Cancel</button>
          <button @click="addPh" class="cb-primary">Add</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CertificatePreview from './CertificatePreview.vue'

export default {
  name: 'CertificateBuilder',
  components: { CertificatePreview },
  props: {
    templateHtml: { type: String, default: null },
    onSave: { type: Function, default: null },
    onChange: { type: Function, default: null },
    isSaveDisabled: { type: Boolean, default: false },
    maxWidth: { type: Number, default: 1150 },
    maxHeight: { type: Number, default: 812 }
  },
  data() {
    return {
      elements: [],
      selectedElement: null,
      orientation: 'landscape', // only landscape supported currently
      canvasBg: '#ffffff',
      scale: 1,
      dragging: false,
      resizing: false,
      dragOff: { x: 0, y: 0 },
      resizeStart: { w: 0, h: 0, x: 0, y: 0 },
      showPhModal: false,
      isPreviewMode: false,
      newPhName: '',
      previewData: {},
      imgMode: 'new',
      fonts: ['Inter', 'Arial', 'Times New Roman', 'Georgia', 'Verdana', 'Helvetica', 'Courier New'],
      idCounter: 0,
      originalHTML: '',
      previewScale: 1,
      previewDimensions: null,
      guides: { centerX: false, centerY: false, vertical: [], horizontal: [] },
      snapThreshold: 5,
      popoverPos: null,
      editingPlaceholder: null,
      phPopoverPos: null,
      isSaving: false,
      isFullscreen: false
    }
  },
  computed: {
    canvasWidth() { return this.maxWidth },
    canvasHeight() { return this.maxHeight },
    detectedPlaceholders() {
      const html = this.originalHTML || this.generatedHTML
      const matches = html.match(/\{\{([^}]+)\}\}/g) || []
      return [...new Set(matches.map(m => m.replace(/\{\{|\}\}/g, '').trim()))]
    },
    generatedHTML() {
      return `<!DOCTYPE html>
<html><head><meta charset="UTF-8"><title>Certificate</title>
<meta name="builder" content="certificate-builder">
<meta name="page-width" content="${this.canvasWidth}">
<meta name="page-height" content="${this.canvasHeight}">
<meta name="orientation" content="${this.orientation}">
<link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&display=swap" rel="stylesheet">
<style>
.certificate{position:relative;width:${this.canvasWidth}px;height:${this.canvasHeight}px;background:${this.canvasBg};margin:0 auto;overflow:hidden}
.certificate img{position:absolute}.certificate .t{position:absolute}
</style></head><body><div class="certificate">
${this.elements.map(el => {
  if (el.type === 'image') return `<img src="${el.src}" data-type="image" data-x="${el.x}" data-y="${el.y}" data-w="${el.width}" data-h="${el.height}" style="left:${el.x}px;top:${el.y}px;width:${el.width}px;height:${el.height}px"/>`
  const wrapStyle = el.width > 0 ? `width:${el.width}px;word-wrap:break-word;overflow-wrap:break-word` : 'white-space:nowrap'
  return `<div class="t" data-type="${el.type}" data-x="${el.x}" data-y="${el.y}" data-w="${el.width || 0}" data-font-size="${el.fontSize}" data-font-family="${el.fontFamily}" data-color="${el.color}" data-font-weight="${el.fontWeight}" data-font-style="${el.fontStyle}" data-text-align="${el.textAlign || 'left'}" style="left:${el.x}px;top:${el.y}px;font-size:${el.fontSize}px;font-family:${el.fontFamily};color:${el.color};font-weight:${el.fontWeight};font-style:${el.fontStyle};text-align:${el.textAlign || 'left'};${wrapStyle}">${this.esc(el.content)}</div>`
}).join('\n')}
</div></body></html>`
    },
    popoverStyle() {
      if (!this.popoverPos) return { display: 'none' }
      return { left: this.popoverPos.x + 'px', top: this.popoverPos.y + 'px' }
    },
    filledPlaceholders() {
      // Only pass non-empty values so unfilled {{Key}} stays visible and clickable
      const result = {}
      for (const key of Object.keys(this.previewData)) {
        if (this.previewData[key]) result[key] = this.previewData[key]
      }
      return result
    },
    phPopoverStyle() {
      if (!this.phPopoverPos) return { display: 'none' }
      return { left: this.phPopoverPos.x + 'px', top: this.phPopoverPos.y + 'px' }
    },
    previewContent() {
      // Always use generatedHTML so preview stays in sync with canvas edits.
      // originalHTML is stale after any property change (x, y, font, text, etc.)
      const html = this.generatedHTML
      // Strip document wrapper (<!DOCTYPE>, <html>, <head>, <body>, <title>, <meta>, <link>)
      // that render as visible content when injected via v-html, causing false overflow.
      // Keep only <style> blocks + body inner content.
      let styles = ''
      const styleMatches = html.match(/<style[\s\S]*?<\/style>/gi)
      if (styleMatches) styles = styleMatches.join('\n')
      // Remove overflow:hidden so CertificatePreview can detect elements that
      // extend beyond the certificate bounds (export HTML keeps overflow:hidden)
      styles = styles.replace(/overflow\s*:\s*hidden/gi, 'overflow:visible')
      const bodyMatch = html.match(/<body[^>]*>([\s\S]*)<\/body>/i)
      const bodyContent = bodyMatch ? bodyMatch[1] : html
      return styles + '\n' + bodyContent
    }
  },
  watch: {
    isPreviewMode() { this.$nextTick(this.calcPreviewScale) },
    selectedElement() { this.$nextTick(this.updatePopoverPosition) },
    templateHtml: {
      immediate: true,
      handler(html) {
        if (html) this.$nextTick(() => this.parseTemplate(html))
      }
    },
  },
  mounted() {
    this.calcScale()
    window.addEventListener('resize', this.onResize)
    document.addEventListener('fullscreenchange', this.onFullscreenChange)
    // ResizeObserver catches layout changes that window.resize misses
    // (parent modal animating in, v-show toggling, flex reflow, etc.)
    if (this.$refs.canvasArea && typeof ResizeObserver !== 'undefined') {
      this._resizeObserver = new ResizeObserver(() => this.calcScale())
      this._resizeObserver.observe(this.$refs.canvasArea)
    }

    // load templete here later if any templete need to load
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
    document.removeEventListener('fullscreenchange', this.onFullscreenChange)
    if (this._resizeObserver) {
      this._resizeObserver.disconnect()
      this._resizeObserver = null
    }
  },
  methods: {
    onResize() {
      this.calcScale()
      if (this.isPreviewMode) this.calcPreviewScale()
      this.updatePopoverPosition()
    },
    calcScale() {
      if (!this.$refs.canvasArea) return
      const a = this.$refs.canvasArea
      const w = a.clientWidth - 32, h = a.clientHeight - 32
      this.scale = Math.min(w / this.canvasWidth, h / this.canvasHeight, 1)
    },
    calcPreviewScale() {
      if (!this.$refs.previewArea) return
      const a = this.$refs.previewArea
      const w = a.clientWidth - 64, h = a.clientHeight - 64
      this.previewScale = Math.min(w / this.canvasWidth, h / this.canvasHeight, 1)
    },
    esc(t) { const d = document.createElement('div'); d.textContent = t; return d.innerHTML },
    notifyChange() { if (this.onChange) this.onChange() },
    rgbToHex(rgb) {
      if (!rgb || rgb.startsWith('#')) return rgb
      const m = rgb.match(/(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/)
      if (!m) return rgb
      return '#' + [m[1], m[2], m[3]].map(x => parseInt(x).toString(16).padStart(2, '0')).join('')
    },
    genId() { return ++this.idCounter },

    updatePopoverPosition() {
      if (!this.selectedElement || !this.$refs.canvas || !this.$refs.canvasArea) {
        this.popoverPos = null
        return
      }
      const el = this.selectedElement
      const canvasRect = this.$refs.canvas.getBoundingClientRect()
      const areaRect = this.$refs.canvasArea.getBoundingClientRect()
      const elScreenX = canvasRect.left - areaRect.left + el.x * this.scale
      const elScreenY = canvasRect.top - areaRect.top + el.y * this.scale
      const elW = (el.type === 'image' ? el.width : (el.width || 100)) * this.scale
      const elH = (el.type === 'image' ? el.height : (el.fontSize || 20)) * this.scale
      const popW = 250
      const popH = el.type === 'image' ? 120 : 340
      // Try right of element
      let left = elScreenX + elW + 16
      let top = elScreenY
      // If overflow right, try left
      if (left + popW > areaRect.width) {
        left = elScreenX - popW - 16
      }
      // Clamp left
      left = Math.max(8, left)
      // Clamp top — ensure popover doesn't go below canvas area
      if (top + popH > areaRect.height - 8) {
        top = areaRect.height - popH - 8
      }
      top = Math.max(8, top)
      this.popoverPos = { x: left, y: top }
    },

    onPreviewAreaClick(e) {
      if (e.target.closest('.ph-popover')) return

      // Only react to placeholder spans with data-ph attribute
      const phEl = e.target.closest('[data-ph]')
      if (phEl) {
        const name = phEl.getAttribute('data-ph')
        if (name && this.detectedPlaceholders.includes(name)) {
          this.editingPlaceholder = name
          if (!(name in this.previewData)) {
            this.$set(this.previewData, name, '')
          }
          const areaRect = this.$refs.previewArea.getBoundingClientRect()
          const targetRect = phEl.getBoundingClientRect()
          const popW = 220
          const popH = 80
          let left = targetRect.left - areaRect.left
          let top = targetRect.bottom - areaRect.top + 8
          if (left + popW > areaRect.width - 8) {
            left = areaRect.width - popW - 8
          }
          if (top + popH > areaRect.height - 8) {
            top = targetRect.top - areaRect.top - popH - 8
          }
          left = Math.max(8, left)
          top = Math.max(8, top)
          this.phPopoverPos = { x: left, y: top }
          this.$nextTick(() => {
            if (this.$refs.phPopoverInput) this.$refs.phPopoverInput.focus()
          })
          return
        }
      }

      // Clicked outside any placeholder — close popover
      this.editingPlaceholder = null
      this.phPopoverPos = null
    },

    togglePreview() {
      if (!this.isPreviewMode) {
        this.detectedPlaceholders.forEach(p => {
          if (!(p in this.previewData)) {
            this.$set(this.previewData, p, '')
          }
        })
      }
      this.editingPlaceholder = null
      this.phPopoverPos = null
      this.isPreviewMode = !this.isPreviewMode
    },

    onPlaceholderInput(key, value) {
      this.$set(this.previewData, key, value)
    },

    addPlaceholder() { this.newPhName = ''; this.showPhModal = true; this.$nextTick(() => this.$refs.phInput?.focus()) },
    addPh() {
      const n = this.newPhName.trim()
      if (n) {
        const el = { id: this.genId(), type: 'placeholder', placeholderName: n, content: `{{${n}}}`, x: 40, y: 40 + this.elements.length * 35, width: 0, fontSize: 24, fontFamily: 'Inter', color: '#333', fontWeight: 'normal', fontStyle: 'normal', textAlign: 'left' }
        this.elements.push(el)
        this.selectedElement = el
        this.originalHTML = ''
        this.notifyChange()
      }
      this.showPhModal = false
    },
    addText() {
      const el = { id: this.genId(), type: 'text', content: 'Text', x: 40, y: 40 + this.elements.length * 35, width: 0, fontSize: 20, fontFamily: 'Inter', color: '#000', fontWeight: 'normal', fontStyle: 'normal', textAlign: 'left' }
      this.elements.push(el)
      this.selectedElement = el
      this.originalHTML = ''
      this.notifyChange()
    },
    addImage() { this.imgMode = 'new'; this.$refs.imgInput.click() },
    replaceImage() { this.imgMode = 'replace'; this.$refs.imgInput.click() },
    onImageFile(e) {
      const f = e.target.files[0]
      if (!f) return
      const r = new FileReader()
      r.onload = ev => {
        const b64 = ev.target.result
        if (this.imgMode === 'replace' && this.selectedElement?.type === 'image') {
          this.selectedElement.src = b64
        } else {
          const img = new Image()
          img.onload = () => {
            let w = img.width, h = img.height
            if (w > 150 || h > 150) { const r = Math.min(150 / w, 150 / h); w = Math.round(w * r); h = Math.round(h * r) }
            this.elements.push({ id: this.genId(), type: 'image', src: b64, x: 40, y: 40, width: w, height: h })
            this.selectedElement = this.elements[this.elements.length - 1]
          }
          img.src = b64
        }
        this.originalHTML = ''
        this.notifyChange()
      }
      r.readAsDataURL(f)
      e.target.value = ''
    },

    // Smart guide helpers
    getElementEdges(el) {
      const left = el.x
      const top = el.y
      let w, h
      if (el.type === 'image') {
        w = el.width
        h = el.height
      } else {
        const domEl = this.$refs.canvas && this.$refs.canvas.querySelector(`[data-id="${el.id}"]`)
        if (domEl) {
          w = domEl.offsetWidth
          h = domEl.offsetHeight
        } else {
          w = el.width > 0 ? el.width : 100
          h = el.fontSize * 1.4
        }
      }
      const right = left + w
      const bottom = top + h
      const centerX = (left + right) / 2
      const centerY = (top + bottom) / 2
      return { left, right, top, bottom, centerX, centerY }
    },

    findSnapPoints(el) {
      const edges = this.getElementEdges(el)
      const guides = { centerX: false, centerY: false, vertical: [], horizontal: [] }
      let snapX = null, snapY = null

      const canvasCenterX = this.canvasWidth / 2
      const canvasCenterY = this.canvasHeight / 2

      if (Math.abs(edges.centerX - canvasCenterX) < this.snapThreshold) {
        guides.centerX = true
        snapX = canvasCenterX - (edges.centerX - el.x)
      }
      if (Math.abs(edges.centerY - canvasCenterY) < this.snapThreshold) {
        guides.centerY = true
        snapY = canvasCenterY - (edges.centerY - el.y)
      }

      for (const other of this.elements) {
        if (other.id === el.id) continue
        const otherEdges = this.getElementEdges(other)

        for (const myEdge of [edges.left, edges.centerX, edges.right]) {
          for (const otherEdge of [otherEdges.left, otherEdges.centerX, otherEdges.right]) {
            if (Math.abs(myEdge - otherEdge) < this.snapThreshold) {
              guides.vertical.push(otherEdge)
              if (snapX === null) snapX = otherEdge - (myEdge - el.x)
            }
          }
        }

        for (const myEdge of [edges.top, edges.centerY, edges.bottom]) {
          for (const otherEdge of [otherEdges.top, otherEdges.centerY, otherEdges.bottom]) {
            if (Math.abs(myEdge - otherEdge) < this.snapThreshold) {
              guides.horizontal.push(otherEdge)
              if (snapY === null) snapY = otherEdge - (myEdge - el.y)
            }
          }
        }
      }

      return { guides, snapX, snapY }
    },

    startDrag(el, e) {
      this.selectedElement = el
      this.dragging = true
      const r = this.$refs.canvas.getBoundingClientRect()
      this.dragOff = { x: (e.clientX - r.left) / this.scale - el.x, y: (e.clientY - r.top) / this.scale - el.y }
    },
    startResize(e) {
      this.resizing = true
      this.resizeStart = { w: this.selectedElement.width, h: this.selectedElement.height, x: e.clientX, y: e.clientY }
    },
    onCanvasMouseDown(e) { if (e.target === this.$refs.canvas) this.selectedElement = null },
    onMouseMove(e) {
      if (this.resizing && this.selectedElement?.type === 'image') {
        this.selectedElement.width = Math.max(20, Math.round(this.resizeStart.w + (e.clientX - this.resizeStart.x) / this.scale))
        this.selectedElement.height = Math.max(20, Math.round(this.resizeStart.h + (e.clientY - this.resizeStart.y) / this.scale))
        return
      }
      if (!this.dragging || !this.selectedElement) return

      const r = this.$refs.canvas.getBoundingClientRect()
      let newX = Math.max(0, Math.round((e.clientX - r.left) / this.scale - this.dragOff.x))
      let newY = Math.max(0, Math.round((e.clientY - r.top) / this.scale - this.dragOff.y))

      this.selectedElement.x = newX
      this.selectedElement.y = newY

      const { guides, snapX, snapY } = this.findSnapPoints(this.selectedElement)
      this.guides = guides

      if (snapX !== null) newX = snapX
      if (snapY !== null) newY = snapY

      this.selectedElement.x = Math.max(0, newX)
      this.selectedElement.y = Math.max(0, newY)
      this.originalHTML = ''
    },
    onMouseUp() {
      const wasDragging = this.dragging || this.resizing
      this.dragging = false
      this.resizing = false
      this.guides = { centerX: false, centerY: false, vertical: [], horizontal: [] }
      this.$nextTick(this.updatePopoverPosition)
      if (wasDragging) this.notifyChange()
    },

    deleteElement() {
      if (!this.selectedElement) return
      const i = this.elements.findIndex(e => e.id === this.selectedElement.id)
      if (i > -1) { this.elements.splice(i, 1); this.selectedElement = null }
      this.originalHTML = ''
      this.notifyChange()
    },

    getStyle(el) {
      const s = { position: 'absolute', left: el.x + 'px', top: el.y + 'px', cursor: 'move', userSelect: 'none' }
      if (el.type !== 'image') {
        Object.assign(s, { fontSize: el.fontSize + 'px', fontFamily: el.fontFamily, color: el.color, fontWeight: el.fontWeight, fontStyle: el.fontStyle, textAlign: el.textAlign || 'left' })
        if (el.width > 0) {
          s.width = el.width + 'px'
          s.wordWrap = 'break-word'
          s.overflowWrap = 'break-word'
        } else {
          s.whiteSpace = 'nowrap'
        }
      }
      return s
    },

    urlToBase64(url) {
      return new Promise(resolve => {
        const img = new Image()
        img.crossOrigin = 'anonymous'
        img.onload = () => {
          const canvas = document.createElement('canvas')
          canvas.width = img.width
          canvas.height = img.height
          canvas.getContext('2d').drawImage(img, 0, 0)
          try { resolve(canvas.toDataURL('image/png')) }
          catch { resolve(url) }
        }
        img.onerror = () => resolve(url)
        img.src = url
      })
    },

    async handleSave() {
      if (this.isSaving) return
      const html = this.getHTML()
      if (this.onSave) {
        this.isSaving = true
        try { await this.onSave(html) }
        catch (e) { /* parent handles errors */ }
        finally { this.isSaving = false }
      } else {
        this.downloadTemplate()
      }
    },

    downloadTemplate() {
      const html = this.originalHTML || this.generatedHTML
      const a = document.createElement('a')
      a.href = URL.createObjectURL(new Blob([html], { type: 'text/html' }))
      a.download = 'certificate.html'
      a.click()
    },

    toggleFullscreen() {
      const el = this.$el
      if (!document.fullscreenElement) {
        el.requestFullscreen().catch(() => {})
      } else {
        document.exitFullscreen().catch(() => {})
      }
    },

    onFullscreenChange() {
      this.isFullscreen = !!document.fullscreenElement
      this.$nextTick(() => {
        this.calcScale()
        if (this.isPreviewMode) this.calcPreviewScale()
      })
    },

    loadTemplate() { this.$refs.tplInput.click() },
    onTemplateFile(e) {
      const f = e.target.files[0]
      if (!f) return
      const r = new FileReader()
      r.onload = ev => this.parseTemplate(ev.target.result)
      r.readAsText(f)
      e.target.value = ''
    },

    async parseTemplate(html) {
      this.originalHTML = html

      const doc = new DOMParser().parseFromString(html, 'text/html')
      const cert = doc.querySelector('.certificate') || doc.querySelector('.certificate-container') || doc.body

      const builderMeta = doc.querySelector('meta[name="builder"][content="certificate-builder"]')

      if (builderMeta) {
        const pw = doc.querySelector('meta[name="page-width"]')
        const ph = doc.querySelector('meta[name="page-height"]')
        if (pw && ph) {
          this.previewDimensions = { width: parseInt(pw.content), height: parseInt(ph.content) }
        }
        this.elements = []
        this.idCounter = 0
        await this.parseBuilderElements(cert)
      } else {
        const dimMatch = html.match(/\.certificate[^{]*\{[^}]*width:\s*(\d+)px[^}]*height:\s*(\d+)px/)
        if (dimMatch) {
          this.previewDimensions = { width: parseInt(dimMatch[1]), height: parseInt(dimMatch[2]) }
        } else {
          const containerStyle = html.match(/\.certificate-container[^{]*\{([^}]+)\}/s)
          if (containerStyle) {
            this.previewDimensions = { width: this.maxWidth, height: this.maxHeight }
          }
        }

        const cssRules = {}
        const styleTag = doc.querySelector('style')
        if (styleTag) {
          const cssText = styleTag.textContent
          const ruleMatches = cssText.matchAll(/\.([a-zA-Z0-9_-]+)\s*\{([^}]+)\}/g)
          for (const m of ruleMatches) {
            cssRules[m[1]] = m[2]
          }
        }

        const getCssValue = (className, prop) => {
          const rule = cssRules[className] || ''
          const m = rule.match(new RegExp(prop + '\\s*:\\s*([^;]+)', 'i'))
          return m ? m[1].trim() : null
        }

        const usesAbsolutePositioning = html.includes('position:absolute') || html.includes('position: absolute')

        this.elements = []
        this.idCounter = 0

        if (usesAbsolutePositioning) {
          await this.parseAbsoluteElements(cert, cssRules, getCssValue)
        }
        // Fallback: if absolute parsing found no elements (e.g. positions are in
        // CSS classes, not inline styles), try iframe-based measurement instead
        if (!usesAbsolutePositioning || this.elements.length === 0) {
          await this.parseFlexElements(cert, cssRules, getCssValue)
        }
      }

      this.selectedElement = null
      this.$nextTick(this.calcScale)
    },

    async parseBuilderElements(cert) {
      for (const node of cert.querySelectorAll('[data-type]')) {
        const type = node.getAttribute('data-type')
        const x = parseInt(node.getAttribute('data-x')) || 0
        const y = parseInt(node.getAttribute('data-y')) || 0

        if (type === 'image') {
          let src = node.getAttribute('src') || ''
          if (src.startsWith('http')) {
            src = await this.urlToBase64(src)
          }
          this.elements.push({
            id: this.genId(),
            type: 'image',
            src,
            x, y,
            width: parseInt(node.getAttribute('data-w')) || 100,
            height: parseInt(node.getAttribute('data-h')) || 60
          })
        } else {
          const text = node.textContent.trim()
          if (!text) continue
          const isPh = /\{\{.+\}\}/.test(text)
          const el = {
            id: this.genId(),
            type: isPh ? 'placeholder' : 'text',
            content: text,
            x, y,
            width: parseInt(node.getAttribute('data-w')) || 0,
            fontSize: parseInt(node.getAttribute('data-font-size')) || 18,
            fontFamily: (node.getAttribute('data-font-family') || 'Inter').split(',')[0].replace(/['"]/g, '').trim(),
            color: node.getAttribute('data-color') || '#333',
            fontWeight: node.getAttribute('data-font-weight') || 'normal',
            fontStyle: node.getAttribute('data-font-style') || 'normal',
            textAlign: node.getAttribute('data-text-align') || 'left'
          }
          if (isPh) {
            const matches = text.match(/\{\{([^}]+)\}\}/g)
            if (matches && matches.length === 1 && text === matches[0]) {
              el.placeholderName = text.replace(/\{\{|\}\}/g, '')
            } else {
              el.type = 'text'
            }
          }
          this.elements.push(el)
        }
      }
    },

    async parseAbsoluteElements(cert, cssRules, getCssValue) {
      let yOff = 85

      for (const img of cert.querySelectorAll('img')) {
        let src = img.getAttribute('src') || ''
        if (!src) continue

        const isPlaceholder = /^\{\{.+\}\}$/.test(src)
        if (isPlaceholder) {
          const phName = src.replace(/\{\{|\}\}/g, '')
          this.elements.push({
            id: this.genId(), type: 'placeholder', placeholderName: phName, content: src,
            x: this.canvasWidth - 150, y: 30, fontSize: 16, fontFamily: 'Inter',
            color: '#666', fontWeight: 'normal', fontStyle: 'normal', textAlign: 'left'
          })
          continue
        }

        if (src.startsWith('http')) {
          src = await this.urlToBase64(src)
        }

        const st = img.getAttribute('style') || ''
        let x = this.parseNum(st, 'left') || 40
        let y = this.parseNum(st, 'top') || 30
        let w = this.parseNum(st, 'width') || img.width || 100
        let h = this.parseNum(st, 'height') || img.height || 60

        this.elements.push({ id: this.genId(), type: 'image', src, x, y, width: w, height: h })
      }

      for (const el of cert.querySelectorAll('.t, [style*="position:absolute"], [style*="position: absolute"]')) {
        if (el.tagName === 'IMG') continue
        const text = el.textContent.trim()
        if (!text) continue

        const st = el.getAttribute('style') || ''
        const x = this.parseNum(st, 'left') || 40
        const y = this.parseNum(st, 'top') || yOff
        const fontSize = this.parseNum(st, 'font-size') || 18
        const color = this.parseStr(st, 'color') || '#333'
        const fontWeight = this.parseStr(st, 'font-weight') || 'normal'
        const fontStyle = this.parseStr(st, 'font-style') || 'normal'
        const fontFamily = this.parseStr(st, 'font-family') || 'Inter'

        const isPh = /\{\{.+\}\}/.test(text)
        const elem = {
          id: this.genId(),
          type: isPh ? 'placeholder' : 'text',
          content: text,
          x, y, width: this.parseNum(st, 'width') || 0, fontSize,
          fontFamily: fontFamily.split(',')[0].replace(/['"]/g, '').trim(),
          color, fontWeight: fontWeight === '700' ? 'bold' : fontWeight,
          fontStyle, textAlign: 'left'
        }

        if (isPh) {
          const matches = text.match(/\{\{([^}]+)\}\}/g)
          if (matches && matches.length === 1 && text === matches[0]) {
            elem.placeholderName = text.replace(/\{\{|\}\}/g, '')
          } else {
            elem.type = 'text'
          }
        }

        this.elements.push(elem)
        if (!this.parseNum(st, 'top')) yOff += fontSize + 12
      }
    },

    async parseFlexElements(cert, cssRules, getCssValue) {
      const measured = await this.measureElementPositions(this.originalHTML)

      if (measured) {
        // Use iframe-measured container dimensions to compute scale factors
        // so elements are mapped onto the fixed canvas dimensions
        const containerInfo = measured.find(m => m.type === '__container__')
        let scaleX = 1, scaleY = 1
        if (containerInfo && containerInfo.width > 0 && containerInfo.height > 0) {
          this.previewDimensions = { width: Math.round(containerInfo.width), height: Math.round(containerInfo.height) }
          scaleX = this.canvasWidth / containerInfo.width
          scaleY = this.canvasHeight / containerInfo.height
        }

        // Convert CSS background-image to an image element so it appears on canvas
        if (containerInfo && containerInfo.backgroundImage) {
          const urlMatch = containerInfo.backgroundImage.match(/url\(["']?([^"')]+)["']?\)/)
          if (urlMatch) {
            let src = urlMatch[1]
            if (src.startsWith('http')) src = await this.urlToBase64(src)
            this.elements.push({ id: this.genId(), type: 'image', src, x: 0, y: 0, width: this.canvasWidth, height: this.canvasHeight })
          }
        }

        for (const m of measured.filter(m => m.type !== '__container__')) {
          if (m.type === 'image') {
            let src = m.src || ''
            if (!src) continue
            if (src.startsWith('http')) {
              src = await this.urlToBase64(src)
            }
            this.elements.push({
              id: this.genId(),
              type: 'image',
              src,
              x: Math.round(m.x * scaleX), y: Math.round(m.y * scaleY),
              width: Math.round(m.width * scaleX), height: Math.round(m.height * scaleY)
            })
          } else {
            const text = m.text
            if (!text) continue
            const isPh = /\{\{.+\}\}/.test(text)
            const el = {
              id: this.genId(),
              type: isPh ? 'placeholder' : 'text',
              content: text,
              x: Math.round(m.x * scaleX), y: Math.round(m.y * scaleY),
              width: m.width ? Math.round(m.width * scaleX) : 0,
              fontSize: Math.round((m.fontSize || 18) * Math.min(scaleX, scaleY)),
              fontFamily: m.fontFamily || 'Inter',
              color: m.color || '#333',
              fontWeight: m.fontWeight === '700' ? 'bold' : (m.fontWeight || 'normal'),
              fontStyle: m.fontStyle || 'normal',
              textAlign: m.textAlign || 'left'
            }
            if (isPh) {
              const matches = text.match(/\{\{([^}]+)\}\}/g)
              if (matches && matches.length === 1 && text === matches[0]) {
                el.placeholderName = text.replace(/\{\{|\}\}/g, '')
              } else {
                el.type = 'text'
              }
            }
            this.elements.push(el)
          }
        }
      }
    },

    measureElementPositions(html) {
      return new Promise(resolve => {
        const iframe = document.createElement('iframe')
        iframe.style.cssText = `position:fixed;left:-9999px;top:-9999px;width:${this.maxWidth + 50}px;height:${this.maxHeight + 50}px;border:none;visibility:hidden`
        document.body.appendChild(iframe)

        // Write HTML directly — avoid relying on iframe.onload which can
        // race with the initial about:blank load and measure an empty document
        try {
          const iDoc = iframe.contentDocument || iframe.contentWindow.document
          iDoc.open()
          iDoc.write(html)
          iDoc.close()
        } catch (e) {
          document.body.removeChild(iframe)
          resolve(null)
          return
        }

        // Give the browser time to parse, apply CSS, and lay out elements
        setTimeout(() => {
          try {
            const iDoc = iframe.contentDocument || iframe.contentWindow.document
            const results = []
            const container = iDoc.querySelector('.certificate-container') || iDoc.querySelector('.certificate') || iDoc.body

            const containerRect = container.getBoundingClientRect()

            // Capture container dimensions and background image for templates
            // that use CSS background-image and percentage-based sizing
            const containerComputed = iframe.contentWindow.getComputedStyle(container)
            const bgImage = containerComputed.backgroundImage
            results.push({
              type: '__container__',
              width: containerRect.width,
              height: containerRect.height,
              backgroundImage: bgImage !== 'none' ? bgImage : null
            })

            for (const img of container.querySelectorAll('img')) {
              const rect = img.getBoundingClientRect()
              results.push({
                type: 'image',
                src: img.getAttribute('src') || '',
                x: rect.left - containerRect.left,
                y: rect.top - containerRect.top,
                width: rect.width,
                height: rect.height
              })
            }

            const seen = new Set()
            const walkElements = (node) => {
              if (node.nodeType === 1 && !['SCRIPT', 'STYLE', 'IMG', 'TITLE'].includes(node.tagName)) {
                let hasDirectText = false
                let directText = ''
                for (const child of node.childNodes) {
                  if (child.nodeType === 3) {
                    const t = child.textContent.trim()
                    if (t) { hasDirectText = true; directText += t + ' ' }
                  }
                }

                let hasChildElements = false
                for (const child of node.childNodes) {
                  if (child.nodeType === 1 && !['SCRIPT', 'STYLE', 'IMG'].includes(child.tagName)) {
                    hasChildElements = true
                  }
                }

                if (hasDirectText && directText.trim().length > 1) {
                  const text = directText.trim()
                  if (!seen.has(text)) {
                    seen.add(text)
                    const rect = node.getBoundingClientRect()
                    const computed = iframe.contentWindow.getComputedStyle(node)
                    results.push({
                      type: 'text',
                      text,
                      x: rect.left - containerRect.left,
                      y: rect.top - containerRect.top,
                      width: rect.width,
                      fontSize: parseFloat(computed.fontSize),
                      fontFamily: computed.fontFamily.split(',')[0].replace(/['"]/g, '').trim(),
                      color: this.rgbToHex(computed.color),
                      fontWeight: computed.fontWeight,
                      fontStyle: computed.fontStyle,
                      textAlign: computed.textAlign
                    })
                  }
                }

                if (hasChildElements) {
                  for (const child of node.childNodes) {
                    if (child.nodeType === 1) walkElements(child)
                  }
                }
              }
            }
            walkElements(container)

            document.body.removeChild(iframe)
            resolve(results)
          } catch (e) {
            console.error('Failed to measure element positions:', e)
            try { document.body.removeChild(iframe) } catch (_) {}
            resolve(null)
          }
        }, 500)
      })
    },

    parseNum(s, p) { const m = s.match(new RegExp(p + '\\s*:\\s*(\\d+)', 'i')); return m ? parseInt(m[1]) : 0 },
    parseStr(s, p) { const m = s.match(new RegExp(p + '\\s*:\\s*([^;]+)', 'i')); return m ? m[1].trim().replace(/['"]/g, '') : null },

    loadFromHTML(h) { this.parseTemplate(h) },
    getHTML() { return this.originalHTML || this.generatedHTML }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap');

.certificate-builder {
  font-family: 'Inter', sans-serif;
  background: #f5f5f5;
  display: flex;
  height: 100%;
  min-height: 0;
}

/* Sidebar */
.sidebar {
  width: 52px;
  border: 1px solid #e0e0e0;
  background: #fff;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  height: 100%;
  overflow-y: auto;
}

.sidebar-tools {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 6px;
  gap: 1px;
}

.tool-btn {
  width: 38px;
  height: 38px;
  border: none;
  background: none;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #555;
  padding: 0;
  position: relative;
}

.tool-btn:hover:not(:disabled) {
  color: var(--primary-color);
}

.tool-btn:disabled {
  opacity: 0.3;
  cursor: default;
}

.tool-btn svg {
  width: 20px;
  height: 20px;
  fill: currentColor;
}

.preview-btn.active {
  color: var(--primary-color);
}

.sidebar-sep {
  width: 28px;
  height: 1px;
  background: #e0e0e0;
  margin: 4px 0;
}

/* Save spinner */
.save-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid #ddd;
  border-top-color: var(--primary-color);
  border-radius: 50%;
  animation: cb-spin 0.6s linear infinite;
  display: block;
}

@keyframes cb-spin {
  to { transform: rotate(360deg); }
}

/* Fullscreen */
.certificate-builder:fullscreen {
  background: #f5f5f5;
  height: 100vh;
  min-height: 100vh;
}

.certificate-builder:fullscreen .sidebar {
  height: 100vh;
}

.certificate-builder:fullscreen .canvas-area,
.certificate-builder:fullscreen .preview-area {
  height: 100vh;
}



/* Canvas Area */
.canvas-area {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: #e5e5e5;
  overflow: hidden;
  position: relative;
}

.canvas-wrap {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.canvas {
  position: relative;
}

/* Smart Guides */
.guide {
  position: absolute;
  pointer-events: none;
  z-index: 100;
}

.guide-v {
  width: 1px;
  top: 0;
  bottom: 0;
}

.guide-h {
  height: 1px;
  left: 0;
  right: 0;
}

.guide-center {
  background: var(--primary-color);
  z-index: 100;
}

.guide-align {
  background: #f59e0b;
  z-index: 100;
}

.el {
  border: 1px solid transparent;
  padding: 1px 3px;
}

.el.selected {
  border: 1px dashed var(--primary-color);
  background: rgba(37, 99, 235, 0.05);
}

.el img {
  display: block;
  pointer-events: none;
}

.resize {
  position: absolute;
  right: -4px;
  bottom: -4px;
  width: 8px;
  height: 8px;
  background: var(--primary-color);
  border: 1px solid #fff;
  cursor: se-resize;
}

/* Floating Properties Popover */
.props-popover {
  position: absolute;
  width: 250px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  z-index: 50;
  max-height: 80vh;
  overflow-y: auto;
}

.props {
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.prop-row-pair {
  display: flex;
  gap: 8px;
}

.prop-row-pair .prop-row {
  flex: 1;
}

.prop-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.prop-row label {
  font-size: 10px;
  color: #888;
  width: 12px;
}

.prop-row input[type="number"] {
  flex: 1;
  padding: 5px 6px;
  border: 1px solid #ddd;
  border-radius: 3px;
  font-size: 12px;
}

.props select {
  padding: 5px 6px;
  border: 1px solid #ddd;
  border-radius: 3px;
  font-size: 12px;
}

.props textarea {
  padding: 6px;
  border: 1px solid #ddd;
  border-radius: 3px;
  font-size: 12px;
  min-height: 50px;
  resize: vertical;
}

.props input[type="text"] {
  padding: 6px;
  border: 1px solid #ddd;
  border-radius: 3px;
  font-size: 12px;
}

.unit {
  font-size: 10px;
  color: #888;
}

.color {
  width: 28px;
  height: 26px;
  padding: 1px;
  border: 1px solid #ddd;
  border-radius: 3px;
  cursor: pointer;
}

.style-row button {
  width: 24px;
  height: 24px;
  border: 1px solid #ddd;
  background: #fff;
  border-radius: 3px;
  cursor: pointer;
  font-size: 11px;
  font-weight: 600;
  color: #555;
  display: flex;
  align-items: center;
  justify-content: center;
}

.style-row button.i {
  font-style: italic;
}

.style-row button.active {
  background: var(--primary-color);
  border-color: var(--primary-color);
  color: #fff;
}

.style-row button svg {
  width: 12px;
  height: 12px;
  fill: currentColor;
}

.sep-v {
  width: 1px;
  height: 16px;
  background: #ddd;
  margin: 0 2px;
}

.btn-replace {
  padding: 5px 8px;
  border: 1px solid #ddd;
  background: #fafafa;
  border-radius: 3px;
  font-size: 11px;
  cursor: pointer;
}

.btn-replace:hover {
  background: #f0f0f0;
}

/* Preview Area */
.preview-area {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
  background: #e5e5e5;
  overflow: auto;
  position: relative;
}

.preview-container {
  background: #fff;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  border-radius: 4px;
  overflow: hidden;
}

/* Placeholder Popover (Preview Mode) */
.ph-popover {
  position: absolute;
  width: 220px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  z-index: 50;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.ph-popover-label {
  font-size: 11px;
  font-weight: 600;
  color: #555;
}

.ph-popover textarea {
  padding: 6px 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 13px;
  font-family: 'Inter', sans-serif;
  resize: vertical;
  min-height: 36px;
}

.ph-popover textarea:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
}

/* Placeholder Modal — prefixed to avoid leaking into other components */
.cb-modal-bg {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

.cb-modal {
  background: #fff;
  border-radius: 8px;
  width: 340px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
}

.cb-modal-head {
  padding: 12px 16px;
  border-bottom: 1px solid #eee;
  font-weight: 500;
  font-size: 14px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.cb-modal-body {
  padding: 16px;
}

.cb-modal-body input[type="text"] {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 13px;
  font-family: 'Inter', sans-serif;
  line-height: 1.4;
  background: #fff;
  color: #333;
  box-sizing: border-box;
}

.cb-hint {
  margin-top: 8px;
  font-size: 11px;
  color: #888;
}

.cb-hint code {
  background: #f5f5f5;
  padding: 2px 5px;
  border-radius: 3px;
  color: #b45309;
}

.cb-modal-foot {
  padding: 12px 16px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.cb-modal-foot button {
  padding: 6px 14px;
  border-radius: 4px;
  font-size: 12px;
  font-family: 'Inter', sans-serif;
  cursor: pointer;
  line-height: 1.4;
  /* Override global button reset from style.css */
  background: #fff;
  border: 1px solid #ddd;
  color: #555;
}

.cb-modal-foot button.cb-primary {
  background: var(--primary-color);
  border: 1px solid var(--primary-color);
  color: #fff;
}
</style>
